// components/UpcomingSessionCard.tsx
import React, { useMemo } from "react";
import { Stack, Typography, Skeleton } from "@mui/material";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
import { useGetBookings } from "../../../../services/api/booking.api";
import { useAuth } from "../../../../hooks/useAuth";
import { Booking } from "../../../../types/Booking";
import SessionCard from "./SessionCard";

dayjs.extend(minMax);

const UpcomingSessionCard: React.FC = () => {
  const { userInfo } = useAuth();
  const { data, isLoading, error } = useGetBookings({
    key: ["bookings", userInfo?.id],
  });

  const now = dayjs();

  const aggregated = useMemo(() => {
    if (!data?.data) return [];

    // ✅ Step 1: Filter for today’s date, after now, and status is "accepted" or "ongoing"
    const todaysUpcomingBookings = data.data.filter((b: Booking) => {
      const start = dayjs(b.start_date_time);
      const end = dayjs(b.end_date_time);
      const status = b.status.toLowerCase();

      return (
        start.isSame(now, "day") &&
        end.isAfter(now) && // show if it's not yet ended
        (status === "scheduled" || status === "ongoing")
      );
    });

    // ✅ Step 2: Remove duplicates
    const uniqueBookings = todaysUpcomingBookings.filter(
      (b: Booking, index: number, arr: Booking[]) =>
        index ===
        arr.findIndex(
          (other: Booking) =>
            b.card.id === other.card.id &&
            b.start_date_time === other.start_date_time &&
            b.end_date_time === other.end_date_time &&
            b.user_id === other.user_id
        )
    );

    // ✅ Step 3: Sort and optionally group sessions
    const groupedSessions: Booking[][] = [];

    uniqueBookings
      .sort((a: Booking, b: Booking) =>
        dayjs(a.start_date_time).diff(dayjs(b.start_date_time))
      )
      .forEach((booking: Booking) => {
        groupedSessions.push([booking]);
      });

    // ✅ Step 4: Transform to display format
    return groupedSessions.map((bookings) => {
      const [first] = bookings;
      const startTimes = bookings.map((b) => dayjs(b.start_date_time));
      const endTimes = bookings.map((b) => dayjs(b.end_date_time));

      const minTime = dayjs.min(startTimes);
      const maxTime = dayjs.max(endTimes);

      const channelUrl = `/user/session/${first.channel_name}?bookingId=${first?.id}`;
      const spadeChannelUrl = `/user/spade-session/${first.channel_name}?bookingId=${first?.id}&cardId=${first?.card?.id}`;
      const groupChannelUrl = `/user/group-session/${first.channel_name}?bookingId=${first?.id}&cardId=${first?.card?.id}`;

      return {
        id: String(first.id),
        title: first.card.title,
        bookingId: first.id,
        talent: first.talent.username,
        avatar:
          first?.card?.primary_thumbnail ??
          first?.card?.gtp?.profile_image ??
          first?.talent?.profile_image ??
          first.talent.username[0],
        date: dayjs(first.start_date_time).format("DD MMM YYYY"),
        timeRange:
          minTime && maxTime
            ? `${minTime.format("hh:mm A")} - ${maxTime.format("hh:mm A")}`
            : "Time unavailable",
        channel:
          first.invites.length > 0
            ? groupChannelUrl
            : first.card.suit === "Spade"
              ? spadeChannelUrl
              : channelUrl,
      };
    });
  }, [data, now]);

  if (isLoading) {
    return (
      <Stack spacing={2}>
        {[...Array(3)].map((_, idx) => (
          <Skeleton key={idx} variant="rounded" height={120} />
        ))}
      </Stack>
    );
  }
  if (error) {
    return <Typography color="error">Failed to load sessions.</Typography>;
  }

  if (!aggregated.length) {
    return <Typography>No upcoming sessions for the rest of today.</Typography>;
  }

  return (
    <Stack spacing={2}>
      {aggregated.map((session) => (
        <SessionCard
          cardName={session.id}
          key={session.id}
          id={session.id}
          title={session.title}
          talent={session.talent}
          avatar={session.avatar}
          date={session.date}
          timeRange={session.timeRange}
          channel={session.channel}
          filter="today"
          bookingId={session.bookingId}
        />
      ))}
    </Stack>
  );
};

export default UpcomingSessionCard;
